import Vue from 'vue'
import Vuex from 'vuex'
import VueResource from 'vue-resource';
import LocalStorage from 'vue-localstorage';
import { _ } from './extend';
import { data, msg, datetime } from './store_utils';
import { price } from './rr_utils';

Vue.use(Vuex)
Vue.use(VueResource);
Vue.use(LocalStorage, { name: 'ls', bind: true });

export default new Vuex.Store({
  state: {
    path: '',
    pathParams: {},
    /// System,
    login: {
      id: null,
      name: null,
      role: null,
      jwtToken: null
    },
    notifications: {}, // text, level
    spinner: {},
    original_values: {},
    event_handlers: {}, // path: path: action(type, newValue)
    validation_errors: {},
    // system
    outpost: {
      id: null,
      street: null,
      city: null,
      selectedWave: null,
      leader: {
        name: null,
        email: null,
        phone: null
      },
      responsiblePerson: {
        isLeader: false,
        id: null,
        email: null,
        phone: null
      },
      accommodationState: {
        nextStepAt: new Date(2100, 1, 1),
        status: 0,
        quote: 0
      }
    },
    participants: {},
    accommodation: {},
    accommodationOrders: {},
    accommodationTransfers: {},
    teams: {},
    buses: {},
    waves: {},
    shirts: {},
    freeTimeActivities: {},
    brackets: {},
    bracketTeams: {},
    matches: {},
    fields: {},
    // intouchable
    outposts: {},
    payments: {},
    priceModifiers: {},
    config: {
      servantDiscount: 0,
      shirtPrice: 0,
      teamAndRoyRegistrationEndAt: new Date(2020, 1, 1),
      shirtsRegistrationEndAt: new Date(2020, 1, 1),
      // admin
      maxAccommodationFilledWithQuotes: 0,
      accommodationQuoteCountAt: new Date(2100, 1, 1),
      accommodationWithoutQuotes_StartAt: new Date(2100, 1, 1),
      eventFirstDay: new Date(2100, 1, 1),
      eventLastDay: new Date(2100, 1, 1),
      accommodationRegistrationEndAt: new Date(2020, 1, 1),
      phaseOnePublished: false,
      phaseTwoPublished: false,
    },
    // constants
    ages: {},
    categories: {},
    childCategories: {},
    royCategories: {},
    servantTypes: [],
    partilyDays: [],
    // admin-only
    users: {},
    userRoles: [],
  },
  getters: {
    isChanged: state => !data.isEmptyNode(state.original_values),
    isValid: state => data.isEmptyNode(state.validation_errors),
    getAt: state => paths => data.getValueAt(state, paths),
    getOriginalAt: state => paths => data.getValueAt(state.original_values, paths),
    isChangedAt: state => paths => (data.getValueAt(state.original_values, paths) !== undefined),
    isValidAt: state => paths => (data.getValueAt(state.validation_errors, paths) == null),

    currentAge: state => Object.values(state.ages).find(a => a.startAt < new Date() && a.endAt > new Date()),

    myPlayingParticipant: state => _.filter(state.participants, part => part.playingOutpostId == state.outpost.id),
    myFinanceParticipant: state => _.filter(state.participants, part => part.financialOutpostId == state.outpost.id),
    team_members: state => {
      var result = _.groupBy(_.filter(state.participants, participant => participant.teamId != null), participant => participant.teamId)

      // add empty teams
      Object.keys(state.teams).forEach(teamId => {
        if (result[teamId] == null)
          Vue.set(result, teamId, {});
      });

      return result;
    },
    roy: state => {
      var result = _.groupBy(_.filter(state.participants, participant => participant.playingOutpostId == state.outpost.id && participant.isRoy), participant => state.royCategories[participant.categoryId]);

      // add empty categories
      Object.keys(state.categories).forEach(cat => {
        if (result[cat] == null)
          Vue.set(result, cat, {});
      });

      return result;
    },
    myAccommodation: state => {
      return _.filter(state.accommodation, acc => acc.outpostId == state.outpost.id);
    },

    /// financial
    price_participants: (state, getters) => _.sum(getters.myFinanceParticipant, participant => price.participant(state, participant)),
    price_acc: (state, getters) => _.sum(getters.myAccommodation, (acc) => acc.price),
    price_other: state => _.sum(state.priceModifiers, item => item.amount),
    price_shirts: state => _.sum(state.shirts, shirt => shirt) * state.config.shirtPrice,
    price_total: (state, getters) => getters.price_participants + getters.price_acc + getters.price_other + getters.price_shirts,
    paid: state => _.sum(state.payments, pay => pay.amount),
    remains: (state, getters) => getters.price_total - getters.paid,

    // matches
    matchesWithRelatedData: state => {
      var result = state.matches;
      result = _.select(result, m => ({ ...m, bracketTeamA: state.bracketTeams[m.bracketTeamAId], bracketTeamB: state.bracketTeams[m.bracketTeamBId] }));
      result = _.select(result, m => ({ ...m, teamA: state.teams[m.bracketTeamA.teamId], teamB: state.teams[m.bracketTeamB.teamId] }));
      result = _.select(result, m => ({ ...m, sourceBracketA: state.brackets[m.bracketTeamA.bracketId], sourceBracketB: state.brackets[m.bracketTeamB.bracketId] }));
      result = _.select(result, m => ({ ...m, sourceMatchA: state.matches[m.bracketTeamA.resultOfId], sourceMatchB: state.matches[m.bracketTeamB.resultOfId]}));
      result = _.select(result, m => ({ ...m, winner: state.teams[m.winnerId] }));
      result = _.select(result, m => ({ ...m, bracket: state.brackets[m.bracketId] }));
      result = _.select(result, m => ({ ...m, field: state.fields[m.fieldId] }));
      result = _.select(result, m => ({ ...m, judge: state.users[m.judgeId] }));
      result = _.select(result, m => ({ ...m, transmittingJudge: state.users[m.transmittingJudgeId] }));
      result = _.select(result, m => ({ ...m, category: m.bracketTeamA.teamId != null ? m.bracketTeamA.teamId.split('-')[0] : null }));
      return result;
    },

    // bracket
    bracketTeamsGrouped: state => _.groupBy(state.bracketTeams, bt => bt.bracketId),
    bracketTeamsCount: (state, getters) => _.select(getters.bracketTeamsGrouped, g => _.count(g)),
  },
  mutations: {
    setPath: (state, payload) => {
      state.path = payload.path;
      state.pathParams = payload.pathParams;
    },

    addSpinner: (state, payload) => {
      Vue.set(state.spinner, payload, true);
    },
    removeSpinner: (state, payload) => {
      Vue.delete(state.spinner, payload);
    },

    notify: (state, payload) => { // text, level[primary,secondary,warning,danger]
      var key = Math.floor(Math.random() * 1000000000);
      Vue.set(state.notifications, key, payload);
    },
    dismissNotification: (state, key) => {
      if (key !== undefined)
        Vue.delete(state.notifications, key);
      else
        Vue.set(state, 'notifications', {});
    },

    add: (state, { paths, value }) => { // paths, value
      data.add(state, paths, value);
    },
    update: (state, { paths, value }) => { // paths, value
      data.update(state, paths, value);
    },
    remove: (state, { paths }) => { // paths
      data.remove(state, paths);
    },
    sync: (state, { paths, value }) => {
      var currentValue = data.getValueAt(state, paths);
      // add
      if (currentValue == null) {
        data.add(state, paths, value);
        return;
      }
      // update
      Object.keys(value)
        .filter(prop => currentValue[prop] !== value[prop])
        .forEach(prop => data.update(state, [...paths, prop], value[prop]));
      // props to remove
      Object.keys(currentValue)
        .filter(prop => value[prop] === undefined)
        .forEach(prop => data.remove(state, [...paths, prop]));
    },
    reset: (state) => {
      if (!confirm("Opravdu chcete zahodit všechny neuložené změny?")) return;

      data.reset(state);
    },

    addEventHandler: (state, payload) => { // paths ('*' for anything), action, id
      var handlers = [state.event_handlers];

      payload.paths.forEach(path => {
        if (Array.isArray(path)) {
          var newHandlers = [];
          handlers.forEach(h => {
            path.forEach(p => {
              if (h[p] === undefined)
                Vue.set(h, p, {});

              newHandlers.push(h[p]);
            })
          });

          handlers = newHandlers;
        }
        else {
          handlers = handlers.map(h => {
            if (h[path] === undefined)
              Vue.set(h, path, {});

            return h[path];
          });
        }
      });

      handlers.forEach(h => {
        if (h['~'] === undefined)
          Vue.set(h, '~', { [payload.id]: payload.action });
        else
          Vue.set(h['~'], payload.id, payload.action);
      });
    },
    invokeEvent: (state, payload) => { // paths, type, newValue
      data.handleEvent(state.event_handlers, payload.paths, payload.type, payload.newValue);
    },

    addValidationError: (state, payload) => { // paths, message
      var node = data.getOrCreateNodeAt(state.validation_errors, payload.paths);
      node.isWrong = true;
      node.msg = payload.message;
    },
    removeValidationError: (state, payload) => { // paths
      var paths = payload.paths;
      var nodePaths = paths.slice(0, -1);
      var lastPath = paths[paths.length - 1];

      var parent = data.getValueAt(state.validation_errors, nodePaths);
      if (parent == null)
        return;

      Vue.delete(parent, lastPath);
      data.cleanEmptyNodes(state.validation_errors, nodePaths);
    },

    // ?? //
    removeTeam: (state, { teamId }) => { // teamId
      // remove players team
      Object.keys(state.participants).forEach(participantId => {
        var participant = state.participants[participantId];
        if (participant.teamId == teamId)
          data.update(state, ['participants', participantId, 'teamId'], null);
      });

      data.remove(state, ['teams', teamId]);
    },
    removeLarpTeam: (state, { teamId }) => { // teamId
      // remove players team
      Object.keys(state.participants).forEach(participantId => {
        var participant = state.participants[participantId];
        if (participant.larpTeamId == teamId)
          data.update(state, ['participants', participantId, 'larpTeamId'], null);
      });

      data.remove(state, ['larpTeams', teamId]);
    },

    login: (state, payload) => {
      state.login.id = payload.id;
      state.login.name = payload.name;
      state.login.role = payload.role;
      state.login.jwtToken = payload.jwtToken;

      Vue.ls.set("id", payload.id);
      Vue.ls.set("name", payload.name);
      Vue.ls.set("role", payload.role);
      Vue.ls.set("jwtToken", payload.jwtToken);
    },
    logout: (state, payload) => {
      if (payload !== true && !data.isEmptyNode(state.original_values) && !confirm("Neuložené změny budou ztraceny. Opravdu chcete odhlásit?"))
        return;

      clean(state);

      Vue.ls.remove("id");
      Vue.ls.remove("name");
      Vue.ls.remove("role");
      Vue.ls.remove("jwtToken");
    },
    updateStore: (state, payload) => {
      console.log("Updating store:", payload);

      // parse date
      datetime.parseAll(payload);

      // update store
      Object.keys(payload).forEach(key => {
        Vue.set(state, key, payload[key]);
      });

      accommodationCheck(state);
    },
    save: (state, payload) => {
      // reset changes
      state.original_values = {};
      // update store
      state.paymentQrCodeSvg = payload.paymentQrCodeSvg;
    },
    generatedMatches: (state, { bracket, matches, bracketTeams, graphSvg }) => {
      // clear
      var matchesToRemove = _.filter(state.matches, m => m.bracketId == bracket.id);
      _.forEach(matchesToRemove, m => data.remove(state, ['matches', m.id]));
      var teamsToRemove = _.filter(state.bracketTeams, bt => bt.bracketId == bracket.id);
      _.forEach(teamsToRemove, t => data.remove(state, ['bracketTeams', t.id]));

      // update or add
      matches.forEach(m => data.add(state, ['matches', m.id], m));
      bracketTeams.forEach(bt => data.add(state, ['bracketTeams', bt.id], bt));
      data.update(state, ['bracketGraphSvgs', bracket.id], graphSvg);
    },
    refreshToken: (state, { jwtToken }) => {
      state.login.jwtToken = jwtToken;

      Vue.ls.set("jwtToken", jwtToken);
    }
  },
  actions: {
    init: (context) => {
      var jwtToken = Vue.ls.get("jwtToken");
      var id = Vue.ls.get("id");
      var name = Vue.ls.get("name");
      var role = Vue.ls.get("role");

      if (jwtToken != null) {
        context.state.login.jwtToken = jwtToken;
        context.state.login.id = id;
        context.state.login.name = name;
        context.state.login.role = role;
        context.dispatch("getInitData", { role: context.state.login.role });
      }
    },
    login: (context, payload) => {
      // clear previous notifications
      context.commit('dismissNotification');
      // show spinner
      var requestId = Math.floor(Math.random() * 1000000000);
      context.commit('addSpinner', requestId);
      // send credentials
      Vue.http.post(msg.getUrl() + '/api/authentication/login', payload)
        .finally(() => context.commit('removeSpinner', requestId))
        .then((response) => {
          console.log("Logged");
          // update data
          context.commit("login", {
            id: response.body.id,
            name: response.body.name,
            role: response.body.role,
            jwtToken: response.body.jwtToken
          })
          context.dispatch("getInitData", { role: response.body.role });
        })
        .catch((err) => {
          let text;
          switch (err.status) {
            case 401:
              text = "Špatný e-mail nebo heslo";
              break;
            case 404:
              text = "Server neodpovídá";
              break;
            default:
              console.log("ERROR: ", err);
              text = "Neznámá chyba";
              break;
          }
          context.commit("notify", {
            text: text,
            level: "danger"
          });
        });
    },
    // oldPassword, newPassword
    changePassword: (context, payload) => {
      msg.post('/api/authentication/changePassword', payload, context,
        () => context.commit("notify", { text: "Heslo úspěšně změněno", level: "success" }),
        () => context.commit("notify", { text: "Špatné heslo", level: "danger" }));
    },
    // userId, newPassword
    adminChangePassword: (context, payload) => {
      msg.post('/api/admin/changePassword', payload, context,
        () => context.commit("notify", { text: "Heslo úspěšně změněno", level: "success" }),
        () => context.commit("notify", { text: "Špatné heslo", level: "danger" }));
    },
    getInitData: (context, payload) => {
      msg.get("/api/" + payload.role + "/Init", context, (resp) => {
        context.commit("updateStore", resp);
      });
    },
    save: (context) => {
      // clear notification
      context.state.notifications = {};

      // validate
      if (!context.getters.isValid) {
        data.forEachValue(context.state.validation_errors, (paths, value) => {
          // show only message
          if (paths[paths.length - 1] != 'msg')
            return;

          context.commit('notify', {
            text: value,
            level: 'danger'
          });
        });

        return;
      }
      // get data
      var result = {};
      data.forEachValue(context.state.original_values, (paths) => {
        var parentPaths = paths.slice(0, -1);
        var newValue = data.getValueAt(context.state, parentPaths);
        if (newValue === undefined || newValue === null) {
          newValue = null;
          paths = parentPaths;
        }
        else {
          newValue = data.getValueAt(context.state, paths);
        }

        // convert date
        if (newValue instanceof Date)
          newValue = datetime.dateToString(newValue);

        var node = data.getOrCreateNodeAt(result, paths.slice(0, -1));
        Vue.set(node, paths[paths.length - 1], newValue);
      });

      // send
      console.log("Sending data:", result);
      msg.post(`/api/${context.state.login.role}/Update`, result, context, (resp) => {
        context.commit("save", resp);
      });
    },
    createParticipant: (context, payload) => {
      var newParticipant = {
        id: data.newGuid(),
        name: "",
        surname: "",
        birthDate: null,
        categoryId: "royalRanger",
        teamId: null,
        isTeamConfirmed: false,
        playingOutpostId: context.state.outpost.id,
        financialOutpostId: context.state.outpost.id,
        isFree: false,
        isRoy: false,
        partilyDays: Math.pow(2, context.state.partilyDays.length) - 1,
        isServant: false,
        isServantConfirmed: null,
        servantType: null,
        email: null,
        phone: null,
        createdInAgeId: context.getters.currentAge.id
      };
      if (payload != null) {
        if (payload.name != null) newParticipant.name = payload.name;
        if (payload.surname != null) newParticipant.surname = payload.surname;
        if (payload.teamId != null) newParticipant.teamId = payload.teamId;
        if (payload.email != null) newParticipant.email = payload.email;
        if (payload.phone != null) newParticipant.phone = payload.phone;
        if (payload.birthDate != null) newParticipant.birthDate = payload.birthDate;
      }

      context.commit("add", {
        paths: ["participants", newParticipant.id],
        value: newParticipant
      });

      if (payload != null && payload.birthDate != null)
        context.dispatch('setParticipantCategory', {
          id: newParticipant.id,
          birthDate: datetime.dateToString(payload.birthDate)
        });
    },
    createTeam: (context, { categoryId, participantId }) => {
      msg.get("/api/User/TeamNumber/" + categoryId, context, (resp) => {
        context.commit("add", {
          paths: ["teams", resp.teamId],
          value: {
            id: resp.teamId,
            outpostId: context.state.outpost.id
          }
        });

        // set to participant
        if (participantId != null)
          context.commit("update", {
            paths: ['participants', participantId, 'teamId'],
            value: resp.teamId
          });
      });
    },
    createLarpTeam: (context, { categoryId, participantId }) => {
      msg.get("/api/User/LarpTeamNumber/" + categoryId, context, (resp) => {
        context.commit("add", {
          paths: ["larpTeams", resp.teamId],
          value: {
            id: resp.teamId,
            outpostId: context.state.outpost.id
          }
        });

        // set to participant
        if (participantId != null)
          context.commit("update", {
            paths: ['participants', participantId, 'larpTeamId'],
            value: resp.teamId
          });
      });
    },
    setParticipantCategory: (context, payload) => { // birthDate, id
      msg.get(`/api/${context.state.login.role}/GetCategory/${payload.birthDate}`, context, (resp) => {
        context.commit("update", {
          paths: ['participants', payload.id, 'categoryId'],
          value: resp.categoryId
        });

        // remove team
        var participant = context.state.participants[payload.id];
        if (participant.teamId != null) {
          var teamCat = participant.teamId.split('-')[0];
          if (!context.state.childCategories[teamCat].includes(participant.categoryId))
            context.commit('update', {
              paths: ['participants', participant.id, 'teamId'],
              value: null
            });
        }
      })
    },
    createUser: (context, payload) => { // name, email, password, role
      msg.post(`/api/authentication/create`, payload, context, data => {
        Vue.set(context.state.users, data, {
          id: data,
          name: payload.name,
          email: payload.email,
          phone: '',
          role: context.state.userRoles[payload.role]
        });
      });
    },
    tryOrderAccommodation: (context, payload) => { // accommodationId
      msg.get(`/api/User/CheckRoom`, context, response => {
        // refresh all response
        Object.values(context.state.accommodation).forEach(acc => {
          const BEValue = (response[acc.id] || null);
          const FEChanged = (context.getters.isChangedAt(['accommodation', acc.id, 'outpostId']));
          const FEoriginValue = FEChanged
            ? context.state.original_values.accommodation[acc.id].outpostId
            : acc.outpostId;

          // nothing changed
          if (BEValue == FEoriginValue)
            return;

          if (FEChanged) {
            // change origin
            context.state.original_values.accommodation[acc.id].outpostId = BEValue;
            // change current
            context.commit("update", {
              paths: ["accommodation", acc.id, "outpostId"],
              value: BEValue
            });
          }
          else {
            acc.outpostId = BEValue;
          }
        });

        // set this room
        if (!context.state.accommodation[payload.accommodationId].outpostId) {
          context.commit("update", {
            paths: ["accommodation", payload.accommodationId, "outpostId"],
            value: context.state.outpost.id
          });
        }
        else {
          context.commit("notify", {
            level: "warning",
            text: `Tento pokoj je již obsazen ${context.state.accommodation[payload.accommodationId].outpostId}. PH`
          });
        }
      });
    },
    syncPayments: (context) => {
      msg.get('/api/Admin/SyncPays', context, response => {
        // parse date
        datetime.parseAll(response);
        // update store
        Vue.set(context.state, 'payments', response);
      });
    },
    exportAll: (context) => {
      msg.getDownload('/api/User/Export', context);
    },
    exportAdmin: (context) => {
      msg.getDownload(`/api/Admin/Export`, context);
    },
    exportTournament: (context, { categoryId }) => {
      msg.getDownload(`/api/Admin/ExportTournament?categoryId=${categoryId}`, context);
    },
    exportTournamentPdf: (context, params) => {
      msg.getDownload(`/api/Admin/ExportTournamentPdf?${new URLSearchParams(params).toString()}`, context);
    },
    generateRoundRobinMatches: (context, { bracket, teamIds }) => {
      var data = {
        bracketId: bracket.id,
        teamIds,
      };
      msg.post('/api/Admin/GenerateRoundRobinMatches', data, context, ({ matches, bracketTeams }) => {
        context.commit('generatedMatches', { bracket, matches, bracketTeams });
      });
    },
    advance: (context, { categoryId, phase }) => {
      if (context.getters.isChanged) {
        context.commit('notify', {
          text: "Pro správné fungování nejprve uložte nebo vraťte změny.",
          level: "warning"
        });
        return;
      }

      msg.post(`/api/Admin/AdvanceTeams?categoryId=${categoryId}&phase=${phase}`, {}, context, ({ updatedTeams }) => {
        updatedTeams.forEach(t => context.commit('update', { paths: ['bracketTeams', t.id], value: t }));
      });
    },
    copyMatcheFromPhase0: (context, { categoryId }) => {
      if (context.getters.isChanged) {
        context.commit('notify', {
          text: "Pro správné fungování nejprve uložte nebo vraťte změny.",
          level: "warning"
        });
        return;
      }

      msg.post(`/api/Admin/CopyMatchesToPhaseTwo?categoryId=${categoryId}`, {}, context, ({ updatedMatches }) => {
        updatedMatches.forEach(m => context.commit('update', { paths: ['matches', m.id], value: m }));
      });
    },
    generateEliminationMatches: (context, { bracket }) => {
      var categoryBrackets = _.filter(context.state.brackets, b => b.categoryId == bracket.categoryId && b.phase == bracket.phase - 1);
      var totalAdvancingCount = _.sum(categoryBrackets, b => b.advancingCount);

      var data = {
        type: bracket.type,
        bracketId: bracket.id,
        teamCount: totalAdvancingCount,
      };
      msg.post('/api/Admin/GenerateEliminationMatches', data, context, ({ matches, bracketTeams, graphSvg }) => {
        context.commit('generatedMatches', { bracket, matches, bracketTeams, graphSvg });
      });
    },
    refreshToken: (context) => {
      msg.get('/api/authentication/refreshToken', context, (resp) => {
        context.commit("refreshToken", {
          jwtToken: resp.jwtToken
        });
      });
    }
  }
})

export function clean(state) {
  /// System,
  state.login = {
    name: null,
    role: null,
    jwtToken: null
  };
  state.notifications = {}; // text, level
  state.spinner = {};
  state.original_values = {};
  state.event_handlers = {}; // path: path: action(type, newValue)
  state.validation_errors = {};
  // system
  state.outpost = {
    id: null,
    street: null,
    city: null,
    selectedWave: null,
    leader: {
      name: null,
      email: null,
      phone: null
    },
    responsiblePerson: {
      isLeader: false,
      id: null,
      email: null,
      phone: null
    },
    accommodationState: {
      nextStepAt: new Date(2100, 1, 1),
      status: 0,
      quote: 0
    }
  };
  state.participants = {};
  state.accommodation = {};
  state.accommodationTransfers = {};
  state.teams = {};
  state.buses = {};
  state.waves = {};
  state.shirts = {};
  // intouchable
  state.outposts = {};
  state.payments = {};
  state.priceModifiers = {};
  state.config = {
    servantDiscount: 0,
    shirtPrice: 0,
    // admin
    maxAccommodationFilledWithQuotes: 0,
    accommodationQuoteCountAt: new Date(2100, 1, 1),
    accommodationWithoutQuotes_StartAt: new Date(2100, 1, 1)
  };
  // constants
  state.ages = {};
  state.categories = {};
  state.childCategories = {};
  state.royCategories = {};
  state.servantTypes = [];
  state.partilyDays = [];
  // admin-only
  state.users = {};
  state.ages = {};
  state.userRoles = [];
}
function accommodationCheck(state) {
  Object.values(state.accommodation).forEach(acc => {
    const el = document.getElementById(`g-${acc.id}`);
    if (!el) {
      console.log(`Missing accommodation: ${acc.id}`);
      return;
    }
  });
}
